'use client';

/**
 * SortableListHeader Component
 *
 * A sortable header row for list views (not tied to react-table).
 * Use this for custom list components that need sortable columns.
 */

import Box from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import type React from 'react';
import { useI18n } from '../../hooks/useI18n';
import { getNeonStyles, type NeonAccent } from '../../theme/palette';
import { transitionPresets } from '../../theme/transitions';
import { transientOptions } from '../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export interface SortableColumn {
  /** Unique key for sorting (sent to onSort callback) */
  key: string;
  /** Display label (translation key or string) */
  label: string;
  /** Column width (e.g., '120px', 'auto', '1fr') */
  width?: string;
  /** Text alignment */
  align?: 'left' | 'center' | 'right';
  /** Whether this column is sortable (default: true) */
  sortable?: boolean;
}

export interface SortState {
  /** Currently sorted column key */
  key: string;
  /** Sort direction */
  direction: 'asc' | 'desc';
}

export interface SortableListHeaderProps {
  /** Column definitions */
  columns: SortableColumn[];
  /** Current sort state */
  sort?: SortState;
  /** Called when a column header is clicked */
  onSort?: (key: string, direction: 'asc' | 'desc') => void;
  /** Neon accent color for active sort */
  neonAccent?: NeonAccent;
  /** Show select all checkbox/button area */
  showSelectArea?: boolean;
  /** Select all content (e.g., checkbox or button) */
  selectContent?: React.ReactNode;
}

// ============================================================================
// Icon Components
// ============================================================================

const ArrowUpIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z" />
  </svg>
);

const ArrowDownIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
  </svg>
);

const UnfoldMoreIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M12 5.83L15.17 9l1.41-1.41L12 3 7.41 7.59 8.83 9 12 5.83zm0 12.34L8.83 15l-1.41 1.41L12 21l4.59-4.59L15.17 15 12 18.17z" />
  </svg>
);

// ============================================================================
// Styled Components
// ============================================================================

interface HeaderContainerProps {
  $isDark: boolean;
}

const HeaderContainer = styled(
  Box,
  transientOptions,
)<HeaderContainerProps>(({ theme, $isDark }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 1.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
}));

interface HeaderCellProps {
  $isDark: boolean;
  $sortable: boolean;
  $active: boolean;
  $neonAccent?: NeonAccent;
  $align?: 'left' | 'center' | 'right';
}

const HeaderCell = styled(
  Box,
  transientOptions,
)<HeaderCellProps>(({ theme, $isDark, $sortable, $active, $neonAccent, $align = 'left' }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: $active
      ? neonStyles?.base || theme.palette.primary.main
      : $isDark
        ? 'rgba(255, 255, 255, 0.6)'
        : 'rgba(0, 0, 0, 0.5)',
    justifyContent: $align === 'right' ? 'flex-end' : $align === 'center' ? 'center' : 'flex-start',
    transition: transitionPresets.colors,

    ...($sortable && {
      cursor: 'pointer',
      userSelect: 'none',

      '&:hover': {
        color: neonStyles?.base || theme.palette.primary.main,
      },
    }),
  };
});

interface SortIconProps {
  $active: boolean;
  $neonAccent?: NeonAccent;
  $isDark: boolean;
}

const SortIcon = styled(
  'span',
  transientOptions,
)<SortIconProps>(({ $active, $neonAccent, $isDark }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    display: 'inline-flex',
    alignItems: 'center',
    opacity: $active ? 1 : 0.4,
    transition: transitionPresets.allFast,
    color: $active && neonStyles ? neonStyles.base : 'inherit',
  };
});

const SelectArea = styled(Box)(({ theme }) => ({
  minWidth: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(1),
}));

// ============================================================================
// Component
// ============================================================================

export function SortableListHeader({
  columns,
  sort,
  onSort,
  neonAccent = 'cyan',
  showSelectArea = false,
  selectContent,
}: SortableListHeaderProps): React.ReactElement {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t } = useI18n();

  const handleColumnClick = (column: SortableColumn) => {
    if (!onSort || column.sortable === false) return;

    const newDirection: 'asc' | 'desc' = sort?.key === column.key && sort.direction === 'desc' ? 'asc' : 'desc';

    onSort(column.key, newDirection);
  };

  const renderSortIcon = (column: SortableColumn) => {
    if (column.sortable === false) return null;

    const isActive = sort?.key === column.key;
    const direction = sort?.direction;

    return (
      <SortIcon $active={isActive} $neonAccent={neonAccent} $isDark={isDark}>
        {isActive && direction === 'asc' ? (
          <ArrowUpIcon />
        ) : isActive && direction === 'desc' ? (
          <ArrowDownIcon />
        ) : (
          <UnfoldMoreIcon />
        )}
      </SortIcon>
    );
  };

  return (
    <HeaderContainer $isDark={isDark}>
      {showSelectArea && <SelectArea>{selectContent}</SelectArea>}

      {columns.map((column) => (
        <HeaderCell
          key={column.key}
          $isDark={isDark}
          $sortable={column.sortable !== false && !!onSort}
          $active={sort?.key === column.key}
          $neonAccent={neonAccent}
          $align={column.align}
          onClick={() => handleColumnClick(column)}
          sx={{ flex: column.width || 1 }}
        >
          {t(column.label, { defaultValue: column.label })}
          {renderSortIcon(column)}
        </HeaderCell>
      ))}
    </HeaderContainer>
  );
}

export default SortableListHeader;
